import type { FC } from "react";
import { useEffect, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { PlainTooltip } from "@similarweb/ui-components";

import {
    AnchorCellWrapper,
    ImageIconContainer,
    TooltipContentContainer,
    TooltipImage,
    TooltipImageContainer,
} from "app/components/React/Table/cells/AnchorCell/AnchorCellStyledComponents";
import { TextWithTooltipCell } from "app/components/React/Table/cells/TextWithTooltipCell";
import { translate } from "app/filters/i18nFilter";

const cacheImage = (imageSource: string, onLoad: () => void, onError: () => void): void => {
    const cachedImage = new Image();

    cachedImage.src = imageSource;

    cachedImage.onload = onLoad;
    cachedImage.onerror = onError;
};

const ImageIconTooltip: FC<{ imageSource: string }> = ({ imageSource }) => {
    const [isTooltipEnabled, setIsTooltipEnabled] = useState<boolean>(false);
    const [isImageExists, setIsImageExists] = useState<boolean>(false);

    const onImageLoad = () => {
        setIsTooltipEnabled(true);
        setIsImageExists(true);
    };

    const onImageError = () => {
        setIsTooltipEnabled(true);
        setIsImageExists(false);
    };

    useEffect(() => {
        cacheImage(imageSource, onImageLoad, onImageError);
    }, []);

    const tooltipContent = isImageExists ? (
        <TooltipContentContainer>
            <TooltipImageContainer>
                <TooltipImage
                    alt=""
                    src={imageSource}
                />
            </TooltipImageContainer>
            <a
                href={imageSource}
                target="_blank"
                rel="noreferrer"
            >
                {translate("table.cell.AnchorCell.image_icon.tooltip.open_image")}
            </a>
        </TooltipContentContainer>
    ) : (
        <>
            <strong>{translate("table.cell.AnchorCell.image_icon.tooltip.broken_text.header")}</strong>
            <br />
            {translate("table.cell.AnchorCell.image_icon.tooltip.broken_text.explanation")}
        </>
    );

    const iconColor = colorsPalettes.carbon[isImageExists ? "400" : "200"];

    return (
        <PlainTooltip
            tooltipContent={tooltipContent}
            enabled={isTooltipEnabled}
            maxWidth={230}
            allowHover={isImageExists}
            closeDelay={isImageExists ? 200 : undefined}
        >
            <ImageIconContainer>
                <SWReactIcons
                    iconName="image"
                    size="xs"
                    iconColor={iconColor}
                />
            </ImageIconContainer>
        </PlainTooltip>
    );
};

export const AnchorCell = ({ value, row }: FixmeType) => {
    let imageIcon = null;

    if (row.Image && row.ImageSource) {
        imageIcon = <ImageIconTooltip imageSource={row.ImageSource} />;
    }

    return (
        <AnchorCellWrapper>
            {imageIcon}
            <TextWithTooltipCell value={value} />
        </AnchorCellWrapper>
    );
};
